// utils/helpers.js
import { colorData, SIZE_RANGE } from './config'

/**
 * 获取尺寸范围
 */
export function getSizeRange(categoryLevel1) {
  if (categoryLevel1 === '女鞋') return SIZE_RANGE['女鞋']
  if (categoryLevel1 === '童鞋') return SIZE_RANGE['童鞋']
  return { min: 26, max: 43 }
}

/**
 * 获取颜色值
 */
export function getColorValue(colorName) {
  if (!colorName) return 'transparent'
  for (const group of Object.keys(colorData)) {
    for (const item of colorData[group]) {
      if (item.name === colorName) return item.color
    }
  }
  return 'transparent'
}

/**
 * ✅ 本地日期格式化（避免 toISOString 的 UTC 时区少一天问题）
 * @param {Date|string} date
 * @returns {string} YYYY-MM-DD
 */
export function formatLocalDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * ✅ 本地月份格式化
 * @param {Date|string} date
 * @returns {string} YYYY-MM
 */
export function formatLocalMonth(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/**
 * ✅ 获取今日日期（本地时区）
 */
export function getToday() {
  return formatLocalDate()
}

/**
 * 格式化日期（用于展示，含时间）
 */
export function formatDate(date, simple = false) {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  if (simple) {
    return d.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

/**
 * 格式化金额
 */
export function formatMoney(n) {
  return '¥' + (Number(n) || 0).toFixed(2)
}

/**
 * 格式化文件大小
 */
export function formatSize(bytes) {
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}

/**
 * 获取锁 Key
 */
export function getLockKey(sale, month) {
  return `${sale}||${month}`
}

/**
 * 数据清理（去除 DOM 相关属性）
 */
export function cleanDataForDB(obj) {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Event || obj instanceof HTMLElement || obj instanceof Node ||
    typeof obj === 'function') return undefined
  if (Array.isArray(obj)) {
    return obj.map(item => cleanDataForDB(item)).filter(item => item !== undefined)
  }
  const newObj = {}
  for (const key of Object.keys(obj)) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const val = cleanDataForDB(obj[key])
      if (val !== undefined) newObj[key] = val
    }
  }
  return newObj
}

/**
 * 排序颜色尺码正序
 */
export function sortSizes(sizes) {
  return sizes.sort((a, b) => {
    const colorA = a.color || '';
    const colorB = b.color || '';
    if (colorA !== colorB) return colorA.localeCompare(colorB);
    return (parseInt(a.size) || 0) - (parseInt(b.size) || 0);
  });
}

/** 日期倒序 */
export function sortDate(data) {
  data.sort((a, b) => new Date(b.date) - new Date(a.date));
}

/**
 * 从 getSizeRange 生成具体尺码数组
 */
export function getSizeList(categoryLevel1) {
  const range = getSizeRange(categoryLevel1)
  const sizes = []
  for (let i = range.min; i <= range.max; i++) {
    sizes.push(String(i))
  }
  return sizes
}

/**
 * 从 getSizeRange 生成档位（大中小）
 */
export function getSizeGroups(categoryLevel1) {
  const range = getSizeRange(categoryLevel1)

  if (range.groups && Array.isArray(range.groups)) {
    return range.groups
  }

  const allSizes = getSizeList(categoryLevel1)
  const isKids = categoryLevel1 === '童鞋'

  if (isKids) {
    return [
      { name: '小', sizes: allSizes.slice(0, 4) },
      { name: '中', sizes: allSizes.slice(4, 7) },
      { name: '大', sizes: allSizes.slice(7, 9) },
      { name: '最大', sizes: allSizes.slice(9) },
    ].filter(g => g.sizes.length > 0)
  }

  return [
    { name: '小', sizes: allSizes.slice(0, 3) },
    { name: '中', sizes: allSizes.slice(3, 6) },
    { name: '大', sizes: allSizes.slice(6) },
  ].filter(g => g.sizes.length > 0)
}

// src/utils/assetUrl.js （新增文件）
export function assetUrl(path) {
  if (!path) return path

  if (Array.isArray(path)) {
    return path.map(p => assetUrl(p))
  }

  if (typeof path !== 'string') return ''

  if (path.startsWith('http')) return path

  const base = import.meta.env.VITE_APP_TARGET === 'apk'
    ? (import.meta.env.VITE_SAVE_BASE_URL || '')
    : ''
  return base + path
}